import { bound } from "@frui.ts/helpers";
import { BusyWatcher, Router } from "@frui.ts/screens";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Modules } from "config";
import { computed, observable, reaction } from "mobx";
import EventBus from "services/eventBus";
import LocalizationService from "services/localizationService";
import SecurityService from "services/securityService";
import ConfirmationService from "services/confirmationService";
import NotificationService from "services/notificationService";
import emptyListResponse from "utils/emptyListResponse";
import { isFilterEmpty } from "utils/filters";
import ListViewModel from "../../listViewModel";
import type OutgoingTransferPaymentResponse from "entities/outgoingTransferPaymentResponse";
import BankTransferDetailViewModel from "./bankTransferDetailViewModel";
import OutgoingTransferRepository from "repositories/outgoingTransferRepository";
import OutgoingTransferPaymentFilters, {
  OutgoingTransferPaymentsRequestValidationRules,
} from "entities/outgoingTransferPaymentsRequest";
import { attachAutomaticValidator, hideValidationErrors } from "@frui.ts/validation";
import download from "downloadjs";
import { format } from "date-fns-tz";

import EnumService from "services/enumService";
import OptionsService from "services/optionsService";
import OutgoingTransferPaymentStatus from "entities/outgoingTransferPaymentStatus";
import type LicensePlate from "entities/licensePlate";
import type CountrySelectViewModel from "viewModels/countrySelectViewModel";
import type PagingManagerViewModel from "viewModels/pagingManagerViewModel";

const navigationName = "bank-transfer";

@Router.registerRoute({ name: Router.Self, route: navigationName, children: [BankTransferDetailViewModel] })
export default class BankTransferViewModel extends ListViewModel<
  OutgoingTransferPaymentResponse,
  BankTransferDetailViewModel,
  ScreenBase,
  OutgoingTransferPaymentFilters
> {
  readonly modelName = "outgoingTransferPayment";
  @observable downloadBusyWatcher = new BusyWatcher();
  countrySelectViewModel: CountrySelectViewModel;
  protected abortController?: AbortController;

  constructor(
    protected localizationService: LocalizationService,
    protected eventBus: EventBus,
    protected securityService: SecurityService,
    protected repository: OutgoingTransferRepository,
    protected detailViewModelFactory: ReturnType<typeof BankTransferDetailViewModel.Factory>,
    private pagingManagerViewModelFactory: ReturnType<typeof PagingManagerViewModel.Factory>,
    protected confirmationService: ConfirmationService,
    protected notificationService: NotificationService,
    protected optionsService: OptionsService,
    private enumService: EnumService,
    private countrySelectViewModelFactory: ReturnType<typeof CountrySelectViewModel.Factory>
  ) {
    super();
    this.navigationName = navigationName;
    this.countrySelectViewModel = this.countrySelectViewModelFactory("id");
  }

  onInitialize() {
    this.unsubscriptions.push(
      reaction(
        () => [
          this.filter.officialRecordId,
          this.filter.licensePlate,
          this.filter.accountNumber,
          this.filter.bank,
          this.filter.countryId,
          this.filter.exportedDate?.from,
          this.filter.exportedDate?.to,
          this.filter.isUnidentified,
          this.filter.messageForRecipient,
          this.filter.paidAmount?.from,
          this.filter.paidAmount?.to,
          this.filter.postingDate?.from,
          this.filter.postingDate?.to,
          this.filter.status,
          this.filter.variableSymbol,
        ],
        () => {
          this.filter.fullTextSearchEnabled = false;
        }
      )
    );
    super.onInitialize();
  }

  protected detailParams(itemId: string) {
    const pagingManager = this.pagingManagerViewModelFactory(this, itemId);
    return [pagingManager];
  }

  @computed
  get name() {
    return this.translate("bankTransfer");
  }

  @computed
  get allowWrite() {
    return true;
  }

  @computed
  get allowDelete() {
    return false;
  }

  @computed
  get statusOptions() {
    return this.optionsService.internalEnumOptions(OutgoingTransferPaymentStatus);
  }

  @computed
  get isFilterEmpty() {
    return isFilterEmpty(this.appliedFilter);
  }

  @bound
  allowItemDetail(item: any) {
    return false;
  }

  get allowReportDownload() {
    return (
      this.securityService.allowTo("finance.reports", "write") ||
      (this.securityService.allowTo("finance.reports") && this.securityService.allowTo("finance"))
    );
  }

  countryCode(countryId: string) {
    return this.enumService.value("countries", countryId)?.code ?? "";
  }

  @bound
  download() {
    const downloadAction = async () => {
      const response = await this.repository.getAllOutgoingTransferPaymentsExport(this.filter);
      download((response as any).data, `report-${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
    };
    this.downloadBusyWatcher.watch<void>(downloadAction());
  }

  protected createFilter() {
    return attachAutomaticValidator(new OutgoingTransferPaymentFilters(), OutgoingTransferPaymentsRequestValidationRules);
  }

  protected async fetchData() {
    if (this.isFilterEmpty) {
      return emptyListResponse();
    }

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }

    const { limit, offset } = this.pagingFilter;
    this.abortController = new AbortController();

    const data = await this.repository.getAllOutgoingTransferPayments(
      { limit, offset },
      this.appliedFilter,
      this.abortController.signal
    );
    this.abortController = undefined;

    return data;
  }

  protected resetFilterValues(filter: OutgoingTransferPaymentFilters) {
    super.resetFilterValues(filter);
    hideValidationErrors(this.filter);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.finance, `outgoingPayments.${code}`, size);
  }

  translateOutgoingTransferPaymentStatus(status: OutgoingTransferPaymentStatus) {
    return this.localizationService.translateEnum("outgoingTransferPaymentStatus", status);
  }

  translateModelAttribute(model: "licensePlate", attribute: keyof LicensePlate): string;
  translateModelAttribute(model: "licensePlate", attribute: keyof LicensePlate) {
    return this.localizationService.translateAttribute(model, attribute);
  }
}
